// Small UI helpers (initials, Odoo tag-color palette, date formatting).

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Odoo stores tag colors as an int 0-11. Same twelve hue slots in both themes;
// the actual values live in palette.css (--tag-N-bg / --tag-N-ink) so the theme
// attribute — not this module — decides dark (muted fill + light ink) vs light
// (pastel fill + dark ink). The dark discipline is documented there: chips must
// be quieter than titles — labels, not highlights.
const TAG_SLOTS = 12;
const TAG_PALETTE: Array<{ bg: string; fg: string }> = Array.from({ length: TAG_SLOTS }, (_, i) => ({
  bg: `var(--tag-${i}-bg)`,
  fg: `var(--tag-${i}-ink)`,
}));

export function tagColor(color: number): { bg: string; fg: string } {
  const idx = Number.isInteger(color) && color >= 0 && color < TAG_PALETTE.length ? color : 0;
  return TAG_PALETTE[idx];
}

// Deterministic avatar background from a name. Seven hue slots; per-theme values
// live in palette.css (--avatar-N) — light mode deepens each hue so the white
// initials stay >= 4.5:1. The hash, and therefore each person's hue, is
// identical in both themes.
const AVATAR_SLOTS = 7;
const AVATAR_BG = Array.from({ length: AVATAR_SLOTS }, (_, i) => `var(--avatar-${i})`);
export function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_BG[h % AVATAR_BG.length];
}

export function formatDate(iso: string | null): string {
  if (!iso) return '';
  // Odoo dates come as "YYYY-MM-DD" OR datetimes "YYYY-MM-DD HH:MM:SS" (space-separated).
  const hasTime = iso.includes('T') || iso.includes(' ');
  const d = new Date(hasTime ? iso.replace(' ', 'T') : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  // A date outside the current year SAYS SO. A bare "Sep 5" in an overdue list was
  // September LAST year reading as upcoming — stripping the year hid exactly the
  // thing an overdue list exists to show.
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = '2-digit';
  return d.toLocaleDateString(undefined, opts);
}

/** Humanized due label: "208d late" / "Today" / "Tmrw" / "in 5d" / "Aug 13". */
export function relDueLabel(iso: string | null): string {
  if (!iso) return '';
  const hasTime = iso.includes('T') || iso.includes(' ');
  const d = new Date(hasTime ? iso.replace(' ', 'T') : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((that.getTime() - today.getTime()) / 86_400_000);
  if (diff < 0) {
    const o = -diff;
    if (o < 60) return `${o}d late`;
    if (o < 700) return `${Math.round(o / 30)}mo late`;
    return `${Math.round(o / 365)}y late`;
  }
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tmrw';
  if (diff <= 14) return `in ${diff}d`;
  return formatDate(iso);
}
